import * as React from "react";
import {useEffect, useRef, useState} from "react";
import ReactDatePicker from "react-datepicker";
import {css} from "@emotion/react";
import styled from "@emotion/styled";
import classNames from "classnames";
import pl from "date-fns/locale/pl";
import {isArray} from "lodash";
import {FormErrorMessage, IRange} from "@web2/form-fields";
import {dateTimeFormat, formatPL} from "@web2/string_utils";

export type DatePickerPopperPlacement = "top" | "bottom" | "right" | "left";
type InputSize = "lg" | "sm";

// `react-datepicker` uses date objects, we use strings as form values as it seems easier
// to transform them here to whatever format api consumes. This component can also be handled as a regular Input/InputRange in form logic.
// I considered using only Dates as form values but we would have to later pass configuration
// to `toQueryValues` and `fromQueryValues` (ie. options.dateTimeFormat) to define the date format our Api uses.
// Strings seem more straightforward and we can define api format in component props.
//
// WARNING: onBlur behaviour in `react-datepicker` works weird in range select version - triggers only in some cases, we probably shouldn't use it

type IDatepickerStringsValueType = string | IRange<string>;
type IDatepickerDatesValueType = Date | null | [Date | null, Date | null];

export interface IProps {
    error?: string[] | null;
    name: string;
    onChange: (name: string, value: IDatepickerStringsValueType) => void;
    onAfterChange: (name: string, value: IDatepickerStringsValueType) => void;
    value: IDatepickerStringsValueType;

    selectsRange?: boolean;
    id: string;
    label?: string | JSX.Element;
    labelClassName?: string;
    className?: string;
    groupClassName?: string;
    required?: boolean;
    helpPlacement?: string;
    apiDateFormat?: string;
    presentationDateFormat?: string;
    popperPlacement?: DatePickerPopperPlacement;
    size?: InputSize;
    errorOnBottom?: boolean;
    errorClassName?: string;
    isHorizontal?: boolean;
    isClearable?: boolean;
}

export const DatePicker: React.FC<IProps> = (props) => {
    const apiDateFormat = props.apiDateFormat ? props.apiDateFormat : dateTimeFormat.dashedDateFormat;
    const presentationDateFormat = props.presentationDateFormat ? props.presentationDateFormat : dateTimeFormat.shortDate;

    const [triggerOnAfterChange, setTriggerOnAfterChange] = useState(false);

    // reference used to store form values for use in onAfterChange, onBlur
    const formValuesRef = useRef(props.value);

    useEffect(() => {
        if (triggerOnAfterChange) {
            props.onAfterChange && props.onAfterChange(props.name, formValuesRef.current);
            setTriggerOnAfterChange(false);
        }
    }, [triggerOnAfterChange]);

    const onChange = (value: IDatepickerDatesValueType) => {
        if ((isArray(value) && value[0] === null && value[1] === null) || value === null) {
            // datepicker was cleared
            props.onChange(props.name, {lower: "", upper: ""});
            setTriggerOnAfterChange(true);
            return;
        }

        const startDate =
            isArray(value) && value[0] ? formatPL(value[0], apiDateFormat) : isArray(value) && value === null ? "" : formatPL(value as Date, apiDateFormat);
        const endDate = isArray(value) && value[1] !== null ? formatPL(value[1], apiDateFormat) : "";

        const stringValues = isArray(value) ? {lower: startDate, upper: endDate} : startDate;
        props.onChange(props.name, stringValues);

        if (value && ((isArray(value) && value[1]) || typeof value === "string")) {
            formValuesRef.current = stringValues;
            setTriggerOnAfterChange(true);
        }
    };

    const getValidDatePickerProps = (value: IDatepickerStringsValueType) => {
        // Prepare dates for datepicker: it expects valid Date objects or nulls
        const startString = typeof value !== "string" ? value.lower : value ? value : null;
        const endString = typeof value !== "string" && value.upper.length > 0 ? value.upper : null;

        const startDate = startString ? new Date(startString) : null;
        const endDate = endString ? new Date(endString) : null;

        return {selected: startDate, startDate, endDate};
    };

    const groupClassName = classNames(props.groupClassName);
    const labelClassName = classNames(props.labelClassName);

    return (
        <DatePickerWrapper
            isHorizontal={props.isHorizontal}
            error={props.error}
            errorOnBottom={props.errorOnBottom}
            sizeVariant={props.size as InputSize}
            className={groupClassName}
            isClearable={props.isClearable}
        >
            {props.label && (
                <Label htmlFor={props.id} className={labelClassName} data-testid={`date-picker-label-${props.name}`}>
                    {props.label} {props.required && <span data-testid={`date-picker-required-asterisk-${props.name}`}>*</span>}
                </Label>
            )}

            {props.error && !props.errorOnBottom && <FormErrorMessage className={props.errorClassName} error={props.error} />}

            <ReactDatePicker
                {...getValidDatePickerProps(props.value)}
                dateFormat={presentationDateFormat}
                onChange={onChange}
                id={props.id}
                locale={pl}
                autoComplete="off"
                selectsRange={props.selectsRange}
                className={props.className}
                popperPlacement={props.popperPlacement}
                isClearable={props.isClearable}
            />

            {props.error && props.errorOnBottom && (
                <FormErrorMessage className={props.errorClassName} error={props.error} errorOnBottom={props.errorOnBottom} />
            )}
        </DatePickerWrapper>
    );
};

interface ITheme {
    borders: {
        border_radius_base: string;
    };
    breakpoints: {
        screen_md: string;
    };
    colors: {
        gray_light: string;
        text_color: string;
    };
    date_picker: {
        date_picker_bg: string;
        date_picker_border_color: string;
        date_picker_border_radius: string;
        date_picker_clear_btn_color: string;
        date_picker_color: string;
        date_picker_day_active_bg_color: string;
        date_picker_day_border_radius: string;
        date_picker_day_range_bg_color: string;
        date_picker_day_size: string;
        date_picker_font_size: string;
        date_picker_header_bg: string;
    };
    forms: {
        error_message_color: string;
        error_message_input_box_shadow: string;
        form_group_margin_bottom: string;
        input_bg: string;
        input_border: string;
        input_border_focus: string;
        input_border_radius: string;
        input_box_shadow_focus: string;
        input_color: string;
        input_color_placeholder: string;
        input_font_size_base: string;
        input_font_size_large: string;
        input_font_size_small: string;
        input_font_weight: string;
        input_height_base: string;
        input_height_large: string;
        input_height_small: string;
        input_transition: string;
        label_color: string;
        label_font_size: string;
        label_font_weight: string;
        label_horizontal_margin: string;
        label_vertical_margin: string;
    };
    fonts: {
        line_height_base: string;
        line_height_small: string;
        line_height_large: string;
        font_size_small: string;
    };
    padding: {
        padding_base_vertical: string;
        padding_base_horizontal: string;
        padding_small_vertical: string;
        padding_large_vertical: string;
    };
}

interface IDatePickerWrapperThemeProps {
    isHorizontal?: boolean;
    error?: string[] | null;
    errorOnBottom?: boolean;
    sizeVariant: InputSize;
    isClearable?: boolean;
}

const Label = styled.label`
    text-align: left;
    font-size: ${(props) => props.theme.forms.label_font_size};
    width: 100%;
    margin-bottom: ${(props) => props.theme.forms.label_vertical_margin};
    margin-right: ${(props) => props.theme.forms.label_horizontal_margin};
    font-weight: ${(props) => props.theme.forms.label_font_weight};
    color: ${(props) => props.theme.forms.label_color};
    line-height: ${(props) => props.theme.fonts.line_height_base};
`;

const DatePickerWrapper = styled.div<IDatePickerWrapperThemeProps>`
    display: flex;
    flex-direction: column;
    position: relative;
    margin-bottom: ${(props) => props.theme.forms.form_group_margin_bottom};

    input {
        display: block;
        background-image: none;
        background-color: ${(props) => props.theme.forms.input_bg};
        outline: 0;
        color: ${(props) => props.theme.forms.input_color};
        font-weight: ${(props) => props.theme.forms.input_font_weight};
        line-height: ${(props) => props.theme.fonts.line_height_base};
        transition: ${(props) => props.theme.forms.input_transition};

        width: 100%;
        height: ${(props) => props.theme.forms.input_height_base};
        padding: ${(props) => props.theme.padding.padding_base_vertical}
            ${(props) => (!props.isClearable ? (props) => props.theme.padding.padding_base_horizontal : "4rem")}
            ${(props) => props.theme.padding.padding_base_vertical} ${(props) => props.theme.padding.padding_base_horizontal};
        border: 1px solid ${(props) => props.theme.forms.input_border};
        border-radius: ${(props) => props.theme.forms.input_border_radius};
        font-size: ${(props) => props.theme.forms.input_font_size_base};

        @media (max-width: ${(props) => props.theme.breakpoints.screen_md}) {
            // iOS font size zoom prevent hack
            //Base multiply value + 33.34%;
            font-size: 1.6rem;
            transform-origin: top left;
            transform: scale(0.75); //  12px to 16px

            //  size up
            width: calc(100% * 1.3334);
            height: calc(${(props) => props.theme.forms.input_height_base} * 1.3334);
            padding: calc(${(props) => props.theme.padding.padding_base_vertical} * 1.3334)
                calc(${(props) => (!props.isClearable ? (props) => props.theme.padding.padding_base_horizontal : "4rem")} * 1.3334)
                calc(${(props) => props.theme.padding.padding_base_vertical} * 1.3334) calc(${(props) => props.theme.padding.padding_base_horizontal} * 1.3334);
            border-radius: calc(${(props) => props.theme.forms.input_border_radius} * 1.3334);
            border: calc(1px * 1.3334) solid ${(props) => props.theme.forms.input_border};
        }

        &:active,
        &:focus {
            border: 1px solid ${(props) => props.theme.forms.input_border_focus};
            box-shadow: ${(props) => props.theme.forms.input_box_shadow_focus};
        }

        &::placeholder {
            color: ${(props) => props.theme.forms.input_color_placeholder};
        }

        ${(props) =>
            props.sizeVariant === "sm" &&
            css`
                height: ${props.theme.forms.input_height_small};
                padding: ${props.theme.padding.padding_small_vertical} ${!props.isClearable ? props.theme.padding.padding_base_horizontal : "4rem"}
                    ${props.theme.padding.padding_small_vertical} ${props.theme.padding.padding_base_horizontal};
                font-size: ${props.theme.forms.input_font_size_small};
                line-height: ${props.theme.fonts.line_height_small};

                @media (max-width: ${props.theme.breakpoints.screen_md}) {
                    // iOS font size zoom prevent hack
                    //Base multiply value + 33.34%;
                    transform-origin: top left;
                    transform: scale(0.75);

                    //  size up
                    width: calc(100% * 1.3334);
                    height: calc(${props.theme.forms.input_height_small} * 1.3334);
                    padding: calc(${props.theme.padding.padding_small_vertical} * 1.3334)
                        calc(${!props.isClearable ? props.theme.padding.padding_base_horizontal : "4rem"} * 1.3334)
                        calc(${props.theme.padding.padding_small_vertical} * 1.3334) calc(${props.theme.padding.padding_base_horizontal} * 1.3334);
                }
            `}

        ${(props) =>
            props.sizeVariant === "lg" &&
            css`
                height: ${props.theme.forms.input_height_large};
                padding: ${props.theme.padding.padding_large_vertical} ${!props.isClearable ? props.theme.padding.padding_base_horizontal : "5rem"}
                    ${props.theme.padding.padding_large_vertical} ${props.theme.padding.padding_base_horizontal};
                font-size: ${props.theme.forms.input_font_size_large};
                line-height: ${props.theme.fonts.line_height_large};
            `}
    }

    ${(props) =>
        props.error &&
        props.errorOnBottom &&
        css`
            margin-bottom: 0;
        `}

    ${(props) =>
        props.isHorizontal &&
        css`
            flex-direction: row;
            align-items: center;

            > label {
                flex-grow: 0;
                width: auto;
                margin-bottom: 0;
            }

            > div {
                flex-grow: 1;
            }
        `}
    
    ${(props) =>
        props.error &&
        css`
            input {
                border-color: ${props.theme.forms.error_message_color};
                box-shadow: ${props.theme.forms.error_message_input_box_shadow};

                &:active,
                &:focus {
                    border-color: ${props.theme.forms.error_message_color};
                }
        `}

    .react-datepicker {
        font-size: ${(props) => props.theme.date_picker.date_picker_font_size};
        background-color: ${(props) => props.theme.date_picker.date_picker_bg};
        color: ${(props) => props.theme.colors.text_color};
        border: 1px solid ${(props) => props.theme.date_picker.date_picker_border_color};
        border-radius: ${(props) => props.theme.date_picker.date_picker_border_radius};
        display: inline-block;
        position: relative;
    }

    .react-datepicker__aria-live {
        position: absolute;
        clip-path: circle(0);
        border: 0;
        height: 1px;
        margin: -1px;
        overflow: hidden;
        padding: 0;
        width: 1px;
        white-space: nowrap;
    }

    .react-datepicker__month-read-view--down-arrow,
    .react-datepicker__month-year-read-view--down-arrow,
    .react-datepicker__navigation-icon:before,
    .react-datepicker__year-read-view--down-arrow {
        border-color: ${(props) => props.theme.date_picker.date_picker_border_color};
        border-style: solid;
        border-width: 3px 3px 0 0;
        content: "";
        display: block;
        height: 9px;
        position: absolute;
        top: 6px;
        width: 9px;
    }

    .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle,
    .react-datepicker-popper[data-placement^="top"] .react-datepicker__triangle {
        margin-left: -4px;
        position: absolute;
        width: 0;
    }

    .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle:after,
    .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle:before,
    .react-datepicker-popper[data-placement^="top"] .react-datepicker__triangle:after,
    .react-datepicker-popper[data-placement^="top"] .react-datepicker__triangle:before {
        box-sizing: content-box;
        position: absolute;
        height: 0;
        width: 1px;
        content: "";
        z-index: -1;
        border: 8px solid transparent;
        left: -8px;
    }

    .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle:before,
    .react-datepicker-popper[data-placement^="top"] .react-datepicker__triangle:before {
        border-bottom-color: ${(props) => props.theme.date_picker.date_picker_border_color};
    }

    .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle {
        top: 0;
        margin-top: -8px;
    }

    .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle:after,
    .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle:before {
        border-top: none;
        border-bottom-color: ${(props) => props.theme.date_picker.date_picker_header_bg};
    }

    .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle:after {
        top: 0;
    }

    .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle:before {
        top: -1px;
        border-bottom-color: ${(props) => props.theme.date_picker.date_picker_border_color};
    }

    .react-datepicker-popper[data-placement^="top"] .react-datepicker__triangle {
        bottom: 0;
        margin-bottom: -8px;
    }

    .react-datepicker-popper[data-placement^="top"] .react-datepicker__triangle:after,
    .react-datepicker-popper[data-placement^="top"] .react-datepicker__triangle:before {
        border-bottom: none;
        border-top-color: ${(props) => props.theme.date_picker.date_picker_bg};
    }

    .react-datepicker-popper[data-placement^="top"] .react-datepicker__triangle:after {
        bottom: 0;
    }

    .react-datepicker-popper[data-placement^="top"] .react-datepicker__triangle:before {
        bottom: -1px;
        border-top-color: ${(props) => props.theme.date_picker.date_picker_border_color};
    }

    .react-datepicker--time-only .react-datepicker__triangle {
        left: 35px;
    }

    .react-datepicker--time-only .react-datepicker__time-container {
        border-left: 0;
    }

    .react-datepicker--time-only .react-datepicker__time,
    .react-datepicker--time-only .react-datepicker__time-box {
        border-bottom-left-radius: ${(props) => props.theme.date_picker.date_picker_day_border_radius};
        border-bottom-right-radius: ${(props) => props.theme.date_picker.date_picker_day_border_radius};
    }

    .react-datepicker__triangle {
        position: absolute;
        left: 50px;
    }

    .react-datepicker-popper {
        z-index: 1;
    }

    .react-datepicker-popper[data-placement^="bottom"] {
        padding-top: 10px;
    }

    .react-datepicker-popper[data-placement="bottom-end"] .react-datepicker__triangle,
    .react-datepicker-popper[data-placement="top-end"] .react-datepicker__triangle {
        left: auto;
        right: 50px;
    }

    .react-datepicker-popper[data-placement^="top"] {
        padding-bottom: 10px;
    }

    .react-datepicker-popper[data-placement^="right"] {
        padding-left: 8px;
    }

    .react-datepicker-popper[data-placement^="right"] .react-datepicker__triangle {
        left: auto;
        right: 42px;
    }

    .react-datepicker-popper[data-placement^="left"] {
        padding-right: 8px;
    }

    .react-datepicker-popper[data-placement^="left"] .react-datepicker__triangle {
        left: 42px;
        right: auto;
    }

    .react-datepicker__header {
        text-align: center;
        background-color: ${(props) => props.theme.date_picker.date_picker_header_bg};
        border-bottom: 1px solid ${(props) => props.theme.date_picker.date_picker_border_color};
        border-top-left-radius: ${(props) => props.theme.date_picker.date_picker_border_radius};
        padding: 8px 0;
        position: relative;
    }

    .react-datepicker__header--time {
        padding-bottom: 8px;
        padding-left: 5px;
        padding-right: 5px;
    }

    .react-datepicker__header--time:not(.react-datepicker__header--time--only) {
        border-top-left-radius: 0;
    }

    .react-datepicker__header:not(.react-datepicker__header--has-time-select) {
        border-top-right-radius: ${(props) => props.theme.date_picker.date_picker_border_radius};
    }

    .react-datepicker__month-dropdown-container--scroll,
    .react-datepicker__month-dropdown-container--select,
    .react-datepicker__month-year-dropdown-container--scroll,
    .react-datepicker__month-year-dropdown-container--select,
    .react-datepicker__year-dropdown-container--scroll,
    .react-datepicker__year-dropdown-container--select {
        display: inline-block;
        margin: 0 2px;
    }

    .react-datepicker-time__header,
    .react-datepicker-year-header,
    .react-datepicker__current-month {
        margin-top: 0;
        color: ${(props) => props.theme.date_picker.date_picker_color};
        font-weight: 700;
        font-size: 1.3rem;
    }

    .react-datepicker-time__header {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
    }

    .react-datepicker__navigation {
        align-items: center;
        background: none;
        display: flex;
        justify-content: center;
        text-align: center;
        cursor: pointer;
        position: absolute;
        top: 2px;
        padding: 0;
        border: none;
        z-index: 1;
        height: 32px;
        width: 32px;
        text-indent: -999em;
        overflow: hidden;
    }

    .react-datepicker__navigation--previous {
        left: 2px;
    }

    .react-datepicker__navigation--next {
        right: 2px;
    }

    .react-datepicker__navigation--next--with-time:not(.react-datepicker__navigation--next--with-today-button) {
        right: 85px;
    }

    .react-datepicker__navigation--years {
        position: relative;
        top: 0;
        display: block;
        margin-left: auto;
        margin-right: auto;
    }

    .react-datepicker__navigation--years-previous {
        top: 4px;
    }

    .react-datepicker__navigation--years-upcoming {
        top: -4px;
    }

    .react-datepicker__navigation:hover :before {
        border-color: #a6a6a6;
    }

    .react-datepicker__navigation-icon {
        position: relative;
        top: 2px;
        font-size: 20px;
    }

    .react-datepicker__navigation-icon--next {
        left: -2px;
    }

    .react-datepicker__navigation-icon--next:before {
        transform: rotate(45deg);
        left: -7px;
    }

    .react-datepicker__navigation-icon--previous {
        right: -2px;
    }

    .react-datepicker__navigation-icon--previous:before {
        transform: rotate(225deg);
        right: -7px;
    }

    .react-datepicker__month-container {
        float: left;
    }

    .react-datepicker__year {
        margin: 0.4rem;
        text-align: center;
    }

    .react-datepicker__year-wrapper {
        display: flex;
        flex-wrap: wrap;
        max-width: 180px;
    }

    .react-datepicker__year .react-datepicker__year-text {
        display: inline-block;
        width: 4rem;
        margin: 2px;
    }

    .react-datepicker__month {
        margin: 0.4rem;
        text-align: center;
    }

    .react-datepicker__month .react-datepicker__month-text,
    .react-datepicker__month .react-datepicker__quarter-text {
        display: inline-block;
        width: 4rem;
        margin: 2px;
    }

    .react-datepicker__input-time-container {
        clear: both;
        width: 100%;
        float: left;
        margin: 5px 0 10px 15px;
        text-align: left;
    }

    .react-datepicker__input-time-container .react-datepicker-time__caption,
    .react-datepicker__input-time-container .react-datepicker-time__input-container {
        display: inline-block;
    }

    .react-datepicker__input-time-container .react-datepicker-time__input-container .react-datepicker-time__input {
        display: inline-block;
        margin-left: 10px;
    }

    .react-datepicker__input-time-container .react-datepicker-time__input-container .react-datepicker-time__input input {
        width: auto;
    }

    .react-datepicker__input-time-container .react-datepicker-time__input-container .react-datepicker-time__input input[type="time"]::-webkit-inner-spin-button,
    .react-datepicker__input-time-container
        .react-datepicker-time__input-container
        .react-datepicker-time__input
        input[type="time"]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    .react-datepicker__input-time-container .react-datepicker-time__input-container .react-datepicker-time__input input[type="time"] {
        -moz-appearance: textfield;
    }

    .react-datepicker__input-time-container .react-datepicker-time__input-container .react-datepicker-time__delimiter {
        margin-left: 5px;
        display: inline-block;
    }

    .react-datepicker__time-container {
        float: right;
        border-left: 1px solid ${(props) => props.theme.date_picker.date_picker_border_color};
        width: 85px;
    }

    .react-datepicker__time-container--with-today-button {
        display: inline;
        border: 1px solid ${(props) => props.theme.date_picker.date_picker_border_color};
        border-radius: ${(props) => props.theme.date_picker.date_picker_day_border_radius};
        position: absolute;
        right: -72px;
        top: 0;
    }

    .react-datepicker__time-container .react-datepicker__time {
        position: relative;
        background: ${(props) => props.theme.date_picker.date_picker_bg};
        border-bottom-right-radius: ${(props) => props.theme.date_picker.date_picker_day_border_radius};
    }

    .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box {
        width: 85px;
        overflow-x: hidden;
        margin: 0 auto;
        text-align: center;
        border-bottom-right-radius: ${(props) => props.theme.date_picker.date_picker_day_border_radius};
    }

    .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list {
        list-style: none;
        margin: 0;
        height: calc(195px + 0.85rem);
        overflow-y: scroll;
        padding-right: 0;
        padding-left: 0;
        width: 100%;
        box-sizing: content-box;
    }

    .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item {
        height: 30px;
        padding: 5px 10px;
        white-space: nowrap;
    }

    .react-datepicker__time-container
        .react-datepicker__time
        .react-datepicker__time-box
        ul.react-datepicker__time-list
        li.react-datepicker__time-list-item:hover {
        cursor: pointer;
        background-color: ${(props) => props.theme.date_picker.date_picker_header_bg};
    }

    .react-datepicker__time-container
        .react-datepicker__time
        .react-datepicker__time-box
        ul.react-datepicker__time-list
        li.react-datepicker__time-list-item--selected {
        background-color: ${(props) => props.theme.date_picker.date_picker_day_active_bg_color};
        color: #fff;
        font-weight: 700;
    }

    .react-datepicker__time-container
        .react-datepicker__time
        .react-datepicker__time-box
        ul.react-datepicker__time-list
        li.react-datepicker__time-list-item--selected:hover {
        background-color: ${(props) => props.theme.date_picker.date_picker_day_active_bg_color};
    }

    .react-datepicker__time-container
        .react-datepicker__time
        .react-datepicker__time-box
        ul.react-datepicker__time-list
        li.react-datepicker__time-list-item--disabled {
        color: #ccc;
    }

    .react-datepicker__time-container
        .react-datepicker__time
        .react-datepicker__time-box
        ul.react-datepicker__time-list
        li.react-datepicker__time-list-item--disabled:hover {
        cursor: default;
        background-color: transparent;
    }

    .react-datepicker__week-number {
        color: #ccc;
        display: inline-block;
        width: ${(props) => props.theme.date_picker.date_picker_day_size};
        line-height: ${(props) => props.theme.date_picker.date_picker_day_size};
        text-align: center;
        margin: 0.2rem;
    }

    .react-datepicker__week-number.react-datepicker__week-number--clickable {
        cursor: pointer;
    }

    .react-datepicker__week-number.react-datepicker__week-number--clickable:hover {
        border-radius: ${(props) => props.theme.date_picker.date_picker_day_border_radius};
        background-color: ${(props) => props.theme.date_picker.date_picker_header_bg};
    }

    .react-datepicker__day-names,
    .react-datepicker__week {
        white-space: nowrap;
    }

    .react-datepicker__day-names {
        margin-bottom: -8px;
    }

    .react-datepicker__day,
    .react-datepicker__day-name,
    .react-datepicker__time-name {
        color: ${(props) => props.theme.date_picker.date_picker_color};
        display: inline-block;
        width: ${(props) => props.theme.date_picker.date_picker_day_size};
        line-height: ${(props) => props.theme.date_picker.date_picker_day_size};
        text-align: center;
        margin: 0.2rem;
    }

    .react-datepicker__month--in-range,
    .react-datepicker__month--in-selecting-range,
    .react-datepicker__month--selected,
    .react-datepicker__quarter--in-range,
    .react-datepicker__quarter--in-selecting-range,
    .react-datepicker__quarter--selected {
        border-radius: ${(props) => props.theme.date_picker.date_picker_day_border_radius};
        background-color: ${(props) => props.theme.date_picker.date_picker_day_active_bg_color};
        color: #fff;
    }

    .react-datepicker__month--in-range:hover,
    .react-datepicker__month--in-selecting-range:hover,
    .react-datepicker__month--selected:hover,
    .react-datepicker__quarter--in-range:hover,
    .react-datepicker__quarter--in-selecting-range:hover,
    .react-datepicker__quarter--selected:hover {
        background-color: ${(props) => props.theme.date_picker.date_picker_day_active_bg_color};
    }

    .react-datepicker__month--disabled,
    .react-datepicker__quarter--disabled {
        color: #ccc;
        pointer-events: none;
    }

    .react-datepicker__month--disabled:hover,
    .react-datepicker__quarter--disabled:hover {
        cursor: default;
        background-color: transparent;
    }

    .react-datepicker__day,
    .react-datepicker__month-text,
    .react-datepicker__quarter-text,
    .react-datepicker__year-text {
        cursor: pointer;
    }

    .react-datepicker__day:hover,
    .react-datepicker__month-text:hover,
    .react-datepicker__quarter-text:hover,
    .react-datepicker__year-text:hover {
        border-radius: ${(props) => props.theme.date_picker.date_picker_day_border_radius};
        background-color: ${(props) => props.theme.date_picker.date_picker_header_bg};
    }

    .react-datepicker__day--today,
    .react-datepicker__month-text--today,
    .react-datepicker__quarter-text--today,
    .react-datepicker__year-text--today {
        font-weight: 700;
    }

    .react-datepicker__day--highlighted,
    .react-datepicker__month-text--highlighted,
    .react-datepicker__quarter-text--highlighted,
    .react-datepicker__year-text--highlighted {
        border-radius: ${(props) => props.theme.date_picker.date_picker_day_border_radius};
        background-color: #3dcc4a;
        color: #fff;
    }

    .react-datepicker__day--highlighted:hover,
    .react-datepicker__month-text--highlighted:hover,
    .react-datepicker__quarter-text--highlighted:hover,
    .react-datepicker__year-text--highlighted:hover {
        background-color: #32be3f;
    }

    .react-datepicker__day--highlighted-custom-1,
    .react-datepicker__month-text--highlighted-custom-1,
    .react-datepicker__quarter-text--highlighted-custom-1,
    .react-datepicker__year-text--highlighted-custom-1 {
        color: #f0f;
    }

    .react-datepicker__day--highlighted-custom-2,
    .react-datepicker__month-text--highlighted-custom-2,
    .react-datepicker__quarter-text--highlighted-custom-2,
    .react-datepicker__year-text--highlighted-custom-2 {
        color: green;
    }

    .react-datepicker__day--in-range,
    .react-datepicker__day--in-selecting-range,
    .react-datepicker__day--selected,
    .react-datepicker__month-text--in-range,
    .react-datepicker__month-text--in-selecting-range,
    .react-datepicker__month-text--selected,
    .react-datepicker__quarter-text--in-range,
    .react-datepicker__quarter-text--in-selecting-range,
    .react-datepicker__quarter-text--selected,
    .react-datepicker__year-text--in-range,
    .react-datepicker__year-text--in-selecting-range,
    .react-datepicker__year-text--selected {
        border-radius: ${(props) => props.theme.date_picker.date_picker_day_border_radius};
        background-color: ${(props) => props.theme.date_picker.date_picker_day_active_bg_color};
        color: #fff;
    }

    .react-datepicker__day--in-range:hover,
    .react-datepicker__day--in-selecting-range:hover,
    .react-datepicker__day--selected:hover,
    .react-datepicker__month-text--in-range:hover,
    .react-datepicker__month-text--in-selecting-range:hover,
    .react-datepicker__month-text--selected:hover,
    .react-datepicker__quarter-text--in-range:hover,
    .react-datepicker__quarter-text--in-selecting-range:hover,
    .react-datepicker__quarter-text--selected:hover,
    .react-datepicker__year-text--in-range:hover,
    .react-datepicker__year-text--in-selecting-range:hover,
    .react-datepicker__year-text--selected:hover {
        background-color: ${(props) => props.theme.date_picker.date_picker_day_active_bg_color};
    }

    .react-datepicker__day--keyboard-selected,
    .react-datepicker__month-text--keyboard-selected,
    .react-datepicker__quarter-text--keyboard-selected,
    .react-datepicker__year-text--keyboard-selected {
        border-radius: ${(props) => props.theme.date_picker.date_picker_day_border_radius};
        background-color: ${(props) => props.theme.date_picker.date_picker_day_active_bg_color};
        color: #fff;
    }

    .react-datepicker__day--keyboard-selected:hover,
    .react-datepicker__month-text--keyboard-selected:hover,
    .react-datepicker__quarter-text--keyboard-selected:hover,
    .react-datepicker__year-text--keyboard-selected:hover {
        background-color: ${(props) => props.theme.date_picker.date_picker_day_active_bg_color};
    }

    .react-datepicker__day--in-selecting-range,
    .react-datepicker__month-text--in-selecting-range,
    .react-datepicker__quarter-text--in-selecting-range,
    .react-datepicker__year-text--in-selecting-range {
        background-color: ${(props) => props.theme.date_picker.date_picker_day_range_bg_color};
    }

    .react-datepicker__month--selecting-range .react-datepicker__day--in-range,
    .react-datepicker__month--selecting-range .react-datepicker__month-text--in-range,
    .react-datepicker__month--selecting-range .react-datepicker__quarter-text--in-range,
    .react-datepicker__month--selecting-range .react-datepicker__year-text--in-range {
        background-color: ${(props) => props.theme.date_picker.date_picker_header_bg};
        color: ${(props) => props.theme.date_picker.date_picker_color};
    }

    .react-datepicker__day--disabled,
    .react-datepicker__month-text--disabled,
    .react-datepicker__quarter-text--disabled,
    .react-datepicker__year-text--disabled {
        cursor: default;
        color: #ccc;
    }

    .react-datepicker__day--disabled:hover,
    .react-datepicker__month-text--disabled:hover,
    .react-datepicker__quarter-text--disabled:hover,
    .react-datepicker__year-text--disabled:hover {
        background-color: transparent;
    }

    .react-datepicker__month-text.react-datepicker__month--in-range:hover,
    .react-datepicker__month-text.react-datepicker__month--selected:hover,
    .react-datepicker__month-text.react-datepicker__quarter--in-range:hover,
    .react-datepicker__month-text.react-datepicker__quarter--selected:hover,
    .react-datepicker__quarter-text.react-datepicker__month--in-range:hover,
    .react-datepicker__quarter-text.react-datepicker__month--selected:hover,
    .react-datepicker__quarter-text.react-datepicker__quarter--in-range:hover,
    .react-datepicker__quarter-text.react-datepicker__quarter--selected:hover {
        background-color: ${(props) => props.theme.date_picker.date_picker_day_active_bg_color};
    }

    .react-datepicker__month-text:hover,
    .react-datepicker__quarter-text:hover {
        background-color: ${(props) => props.theme.date_picker.date_picker_header_bg};
    }

    .react-datepicker__input-container {
        position: relative;
        display: inline-block;
        width: 100%;
    }

    .react-datepicker__month-read-view,
    .react-datepicker__month-year-read-view,
    .react-datepicker__year-read-view {
        border: 1px solid transparent;
        border-radius: ${(props) => props.theme.date_picker.date_picker_day_border_radius};
        position: relative;
    }

    .react-datepicker__month-read-view:hover,
    .react-datepicker__month-year-read-view:hover,
    .react-datepicker__year-read-view:hover {
        cursor: pointer;
    }

    .react-datepicker__month-read-view:hover .react-datepicker__month-read-view--down-arrow,
    .react-datepicker__month-read-view:hover .react-datepicker__year-read-view--down-arrow,
    .react-datepicker__month-year-read-view:hover .react-datepicker__month-read-view--down-arrow,
    .react-datepicker__month-year-read-view:hover .react-datepicker__year-read-view--down-arrow,
    .react-datepicker__year-read-view:hover .react-datepicker__month-read-view--down-arrow,
    .react-datepicker__year-read-view:hover .react-datepicker__year-read-view--down-arrow {
        border-top-color: #b3b3b3;
    }

    .react-datepicker__month-read-view--down-arrow,
    .react-datepicker__month-year-read-view--down-arrow,
    .react-datepicker__year-read-view--down-arrow {
        transform: rotate(135deg);
        right: -16px;
        top: 0;
    }

    .react-datepicker__month-dropdown,
    .react-datepicker__month-year-dropdown,
    .react-datepicker__year-dropdown {
        background-color: ${(props) => props.theme.date_picker.date_picker_header_bg};
        position: absolute;
        width: 50%;
        left: 25%;
        top: 30px;
        z-index: 1;
        text-align: center;
        border-radius: ${(props) => props.theme.date_picker.date_picker_day_border_radius};
        border: 1px solid ${(props) => props.theme.date_picker.date_picker_border_color};
    }

    .react-datepicker__month-dropdown:hover,
    .react-datepicker__month-year-dropdown:hover,
    .react-datepicker__year-dropdown:hover {
        cursor: pointer;
    }

    .react-datepicker__month-dropdown--scrollable,
    .react-datepicker__month-year-dropdown--scrollable,
    .react-datepicker__year-dropdown--scrollable {
        height: 150px;
        overflow-y: scroll;
    }

    .react-datepicker__month-option,
    .react-datepicker__month-year-option,
    .react-datepicker__year-option {
        line-height: 20px;
        width: 100%;
        display: block;
        margin-left: auto;
        margin-right: auto;
    }

    .react-datepicker__month-option:first-of-type,
    .react-datepicker__month-year-option:first-of-type,
    .react-datepicker__year-option:first-of-type {
        border-top-left-radius: ${(props) => props.theme.date_picker.date_picker_day_border_radius};
        border-top-right-radius: ${(props) => props.theme.date_picker.date_picker_day_border_radius};
    }

    .react-datepicker__month-option:last-of-type,
    .react-datepicker__month-year-option:last-of-type,
    .react-datepicker__year-option:last-of-type {
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
        border-bottom-left-radius: ${(props) => props.theme.date_picker.date_picker_day_border_radius};
        border-bottom-right-radius: ${(props) => props.theme.date_picker.date_picker_day_border_radius};
    }

    .react-datepicker__month-option:hover,
    .react-datepicker__month-year-option:hover,
    .react-datepicker__year-option:hover {
        background-color: #ccc;
    }

    .react-datepicker__month-option:hover .react-datepicker__navigation--years-upcoming,
    .react-datepicker__month-year-option:hover .react-datepicker__navigation--years-upcoming,
    .react-datepicker__year-option:hover .react-datepicker__navigation--years-upcoming {
        border-bottom-color: #b3b3b3;
    }

    .react-datepicker__month-option:hover .react-datepicker__navigation--years-previous,
    .react-datepicker__month-year-option:hover .react-datepicker__navigation--years-previous,
    .react-datepicker__year-option:hover .react-datepicker__navigation--years-previous {
        border-top-color: #b3b3b3;
    }

    .react-datepicker__month-option--selected,
    .react-datepicker__month-year-option--selected,
    .react-datepicker__year-option--selected {
        position: absolute;
        left: 15px;
    }

    .react-datepicker__close-icon {
        cursor: pointer;
        background-color: transparent;
        border: 0;
        outline: 0;
        position: absolute;
        top: 0;
        right: 0;
        height: 100%;
        display: table-cell;
        vertical-align: middle;
    }

    .react-datepicker__close-icon:after {
        cursor: pointer;
        background-color: transparent;
        color: ${(props) => props.theme.date_picker.date_picker_clear_btn_color};
        border-radius: 50%;
        height: 24px;
        width: 24px;
        padding: 8px;
        font-size: 24px;
        line-height: 1;
        text-align: center;
        display: table-cell;
        vertical-align: middle;
        content: "\\00d7";
    }

    .react-datepicker__today-button {
        background: ${(props) => props.theme.date_picker.date_picker_header_bg};
        border-top: 1px solid ${(props) => props.theme.date_picker.date_picker_border_color};
        cursor: pointer;
        text-align: center;
        font-weight: 700;
        padding: 5px 0;
        clear: left;
    }

    .react-datepicker__portal {
        position: fixed;
        width: 100vw;
        height: 100vh;
        background-color: rgba(0, 0, 0, 0.8);
        left: 0;
        top: 0;
        justify-content: center;
        align-items: center;
        display: flex;
        z-index: 2147483647;
    }

    .react-datepicker__portal .react-datepicker__day,
    .react-datepicker__portal .react-datepicker__day-name,
    .react-datepicker__portal .react-datepicker__time-name {
        width: 3rem;
        line-height: 3rem;
    }

    @media (max-height: 550px), (max-width: 400px) {
        .react-datepicker__portal .react-datepicker__day,
        .react-datepicker__portal .react-datepicker__day-name,
        .react-datepicker__portal .react-datepicker__time-name {
            width: 2rem;
            line-height: 2rem;
        }
    }

    .react-datepicker__portal .react-datepicker-time__header,
    .react-datepicker__portal .react-datepicker__current-month {
        font-size: 1.44rem;
    }
`;
